import { Component, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ChartConfiguration } from 'chart.js';
import { Test } from 'src/app/models/test.model';
import { TestService } from 'src/app/services/test.service';
import { UtilsService } from 'src/app/services/utils.service';
import { TranslateService } from '@ngx-translate/core';
import * as html2pdf from 'html2pdf.js';
import { LoadingService } from 'src/app/services/loading.service';

@Component({
  selector: 'ct-evalualia-report',
  templateUrl: './evalualia-report.component.html',
  styleUrls: ['./evalualia-report.component.scss']
})
export class EvalualiaReportComponent implements OnInit {

  public test: Test;
  public currentDate = new Date();
  public data: number[];
  public times: string[];
  public reportType: 'language' | 'typewriting' | 'general';
  public radarChartLabels: string[];
  public radarChartData: ChartConfiguration<'radar'>['data'];
  public radarChartOptions: ChartConfiguration<'radar'>['options'];
  /* Implementamos traducción al informe */
  /* Requirement 202463: CT: Como negocio necesito tener el informe de la nueva prueba de Amazon con un solo nivel en inglés */
  public language: string;
  private languageId: number;
  /* Fin Implementamos traducción al informe */

  private computedBodyStyle = getComputedStyle(document.body)

  public colors = {
    primaryColor: this.computedBodyStyle.getPropertyValue('--color-primary'),
    secondaryColor: this.computedBodyStyle.getPropertyValue('--color-secondary'),
    tertiaryColor: this.computedBodyStyle.getPropertyValue('--color-tertiary'),
    quaternaryColor: this.computedBodyStyle.getPropertyValue('--color-quaternary'),
    quinaryColor: this.computedBodyStyle.getPropertyValue('--color-quinary'),
    senaryColor: this.computedBodyStyle.getPropertyValue('--color-senary'),
    septenaryColor: this.computedBodyStyle.getPropertyValue('--color-septenary'),
    octonaryColor: this.computedBodyStyle.getPropertyValue('--color-octonary'),
    nonaryColor: this.computedBodyStyle.getPropertyValue('--color-nonary')
  }

  constructor(
    private router: Router,
    public renderer: Renderer2,
    private testService: TestService,
    private utilsService: UtilsService,
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService,
    private loadingService: LoadingService
  ) { }

  ngOnInit(): void {
    this.activatedRoute.snapshot.queryParamMap.get('languageId')
      ? this.languageId = Number(this.activatedRoute.snapshot.queryParamMap.get('languageId'))
      : this.languageId = Number(this.test.reportLanguageId);
    this.language = this.utilsService.getLanguageFromLanguageId(this.languageId);
    this.translateService.use(this.language);
    this.translateService.setDefaultLang(this.language)
    document.documentElement.lang = this.language.split('-')[0];

    const token = this.activatedRoute.snapshot.paramMap.get('token');

    this.testService.getTestReportResults(token).subscribe(test => {
      this.test = test;

      // En el caso de tenerlo seteamos los valores del proyecto
      if (this.test.projectSelected && this.test.projectSelected.projectId) {

        this.colors = {
          primaryColor: this.test.projectSelected.projectPrimaryColor,
          secondaryColor: this.test.projectSelected.projectSecondaryColor,
          tertiaryColor: this.test.projectSelected.projectTertiaryColor,
          quaternaryColor: this.test.projectSelected.projectQuaternaryColor,
          quinaryColor: this.test.projectSelected.projectQuinaryColor,
          senaryColor: this.test.projectSelected.projectSenaryColor,
          septenaryColor: this.test.projectSelected.projectSeptenaryColor,
          octonaryColor: this.test.projectSelected.projectOctonaryColor,
          nonaryColor: this.test.projectSelected.projectNonaryColor
        }
        document.getElementById('reportModule').style.setProperty('--color-primary', this.colors.primaryColor);
        document.getElementById('reportModule').style.setProperty('--color-secondary', this.colors.secondaryColor);
        document.getElementById('reportModule').style.setProperty('--color-tertiary', this.colors.tertiaryColor);
        document.getElementById('reportModule').style.setProperty('--color-quaternary', this.colors.quaternaryColor);
        document.getElementById('reportModule').style.setProperty('--color-quinary', this.colors.quinaryColor);
        document.getElementById('reportModule').style.setProperty('--color-senary', this.colors.senaryColor);
        document.getElementById('reportModule').style.setProperty('--color-septenary', this.colors.septenaryColor);
        document.getElementById('reportModule').style.setProperty('--color-octonary', this.colors.octonaryColor);
        document.getElementById('reportModule').style.setProperty('--color-nonary', this.colors.nonaryColor);
      }

      switch (test.testModel.name.toLowerCase().substring(0, 6)) {
        case 'idioma': this.reportType = 'language'; break;
        case 'mecano': this.reportType = 'typewriting'; break;
        default: this.reportType = 'general'; break;
      }

      if (this.reportType === 'language') {
        this.data = test.areas.map(area => area.score);
        this.times = test.areas.map(area => this.utilsService.secondsToTime(area.time) + '/pr');
        this.radarChartLabels = test.areas.map(area => area.description.toLowerCase());

        this.radarChartData = {
          labels: this.radarChartLabels,
          datasets: [{
            data: this.data,
            backgroundColor: this.colors.tertiaryColor,
            borderColor: this.colors.tertiaryColor,
            pointBackgroundColor: this.colors.primaryColor
          }]
        };

        this.radarChartOptions = {
          maintainAspectRatio: false,
          scales: {
            r: {
              pointLabels: {
                callback: (pointLabel) => [
                  pointLabel,
                  this.data[this.radarChartLabels.findIndex(x => x === pointLabel)] + '% ' +
                  this.times[this.radarChartLabels.findIndex(x => x === pointLabel)]
                ],
                color: this.colors.primaryColor,
                font: {
                  size: 12
                }
              },
              angleLines: { color: this.colors.primaryColor, lineWidth: 1 },
              grid: { color: this.colors.primaryColor, lineWidth: 1 },
              suggestedMin: 0,
              suggestedMax: 100,
              ticks: {
                display: false,
                maxTicksLimit: 1,
                stepSize: 10,
              }
            }
          },
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
          }
        };
      }

    });
  }

  public chartClicked({ event, active }: { event: MouseEvent, active: {}[] }): void {
    console.log(event, active);
  }

  public chartHovered({ event, active }: { event: MouseEvent, active: {}[] }): void {
    console.log(event, active);
  }

  maxLevelApproved() {
    // Si la puntuacion del ultimo nivel es mayor o igual a 50 devolver el ultimo nivel, si no, devolver el penultimo nivel
    const index = this.test.levels.slice(-1)[0].score >= 50 ? -1 : -2;
    return this.test.levels.slice(index)[0].description;
  }

  print() {
    window.scrollTo(0, 0);
    this.renderer.setAttribute(document.getElementById('evalualiaReport'), 'class', 'print');

    let fileName;
    const date = new Date().toJSON().split('T')[0];
    // (R) 224728 CT: Como cliente, consultor y participante necesito tener alineado los nombres de los ficheros PDF

    const initialsName = (this.test.candidate?.name) ? this.utilsService.initials(this.test.candidate) : this.test.candidate.randstadCandidate;

    fileName = this.test.invitationId
      + '_' + initialsName.replace(/\./g, '') + '_'
      + this.translateService.instant('TESTMODELS.EVALUALIATYPES.TYPE.' + this.test.testModel.type).replace(/ /g, '_') + '_'
      + this.translateService.instant('REPORTS.REPORTTYPES.CONSULTANT') + '_' + date + '.pdf';

    return new Promise<void>(resolve => setTimeout(() => {
      let worker = html2pdf().from(document.querySelectorAll('#evalualiaReport')[0]).set({
        pagebreak: { before: '.page, .break', avoid: 'p' },
        html2canvas: { scale: 2 },
        image: { type: 'jpeg', quality: 1 },
        filename: fileName
      }).toPdf();

      const pages = [...Array.from(document.querySelectorAll('#evalualiaReport'))];
      for (const [i, page] of pages.slice(1).entries()) {
        worker = worker.get('pdf').then(pdf => {
          pdf.addPage();
        }).from(page).toContainer().toCanvas().toPdf();
      }

      worker = worker.get('pdf').then(pdf => this.addHeaderAndFooter(pdf)).save().then(() => {
        this.renderer.setStyle(document.querySelector('body'), 'overflow', 'initial');
        document.querySelectorAll('#evalualiaReport').forEach(element => this.renderer.removeAttribute(element, 'class'));
        resolve();
      }).get('pdf').then(pdf => {
        // Escribir el base64 del pdf en el html para obtenerlo desde bowserprint
        document.querySelector('#base64').textContent = pdf.output('datauristring').toString().split('base64,')[1];
        document.querySelector('#base64').classList.add('base64');
      });

      setTimeout(() => {
        this.loadingService.stopLoading();
      }, 1000);
    }, 500));
  }

  private addHeaderAndFooter(pdf) {
    const totalPages = pdf.internal.getNumberOfPages();

    for (let i = 2; i <= totalPages - 1; i++) {
      pdf.setPage(i);
      pdf.setFontSize(10);
      pdf.setTextColor(this.computedBodyStyle.getPropertyValue('--color-tertiary').trim());

      const relevateImg = new Image();
      relevateImg.src = 'assets/images/relevate-talent-assessment-logo.png';
      pdf.addImage(relevateImg, 'png', 15, 10, 55, 18);

      const randstadImg = new Image();
      randstadImg.src = 'assets/images/randstad-icon-pdf.png';
      pdf.addImage(randstadImg, 'png', 15, pdf.internal.pageSize.getHeight() - 19, 8, 4);

      pdf.text(
        (this.test.candidate.name
          ? this.utilsService.initials(this.test.candidate)
          : this.test.candidate.randstadCandidate) + ' | ' + (this.test.invitationId != 0 ? this.test.invitationId : this.test.externalId) + ' | | K/01/SP', 25, pdf.internal.pageSize.getHeight() - 15);

      pdf.text((i).toString(), pdf.internal.pageSize.getWidth() - 15, pdf.internal.pageSize.getHeight() - 15);
    }
  }

}
